import { useTheme } from '@emotion/react';
import {
  Box,
  Button,
  Card,
  Grid,
  Modal,
  Typography,
  useMediaQuery,
} from '@mui/material';
import AppTextField from 'components/input-fields/AppTextField';
import { ButtonText, H3 } from 'components/Typography';
import { X } from 'react-feather';

export default function ManualDataInputModal({
  showModal,
  setShowModal,
  shareholderData,
  companyData,
  handleShareholderChange,
  handleCompanyDataChange,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: isMobile ? '95%' : isTablet ? '500px' : '900px',
    maxHeight: '90vh',
    overflowY: 'auto',
    backgroundColor: theme.palette.primary.white,
    border: 'none',
    boxShadow: 24,
    outline: 'none',
    paddingRight: '30px',
    paddingLeft: '30px',
    scrollbarWidth: 'thin',
    borderRadius: 6,
    '&::-webkit-scrollbar': {
      width: '0.4em',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'transparent',
    },
  };

  const handleClose = () => {
    setShowModal(false);
  };

  const handleSave = () => {
    // Add any save logic here if needed
    setShowModal(false);
  };

  const renderTables = () => (
    <Grid container mt={2} mb={1} xs={12} gap={2}>
      {/* Shareholders Table */}
      <Grid container xs={12} sm={5.8} mb={isMobile ? 2 : 0}>
        <Box
          sx={{
            width: '100%',
            border: `1px solid ${theme.palette.action.disabled}`,
          }}
        >
          <Grid
            container
            sx={{ borderBottom: `1px solid ${theme.palette.action.disabled}` }}
          >
            <Grid
              item
              xs={4}
              sx={{
                p: 1,
                borderRight: `1px solid ${theme.palette.action.disabled}`,
                display: 'flex',
                alignItems: 'center',
                height: '42px',
              }}
            >
              <Typography variant="body2">역할</Typography>
            </Grid>
            <Grid
              item
              xs={4}
              sx={{
                p: 1,
                borderRight: `1px solid ${theme.palette.action.disabled}`,
                display: 'flex',
                alignItems: 'center',
                height: '42px',
              }}
            >
              <Typography variant="body2">주주명</Typography>
            </Grid>
            <Grid
              item
              xs={4}
              sx={{
                p: 1,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Typography variant="body2">분율</Typography>
            </Grid>
          </Grid>

          {shareholderData.map((row, index) => (
            <Grid
              container
              key={index}
              sx={{
                borderBottom:
                  index !== shareholderData.length - 1
                    ? `1px solid ${theme.palette.action.disabled}`
                    : 'none',
              }}
            >
              <Grid
                item
                xs={4}
                sx={{
                  p: 1,
                  borderRight: `1px solid ${theme.palette.action.disabled}`,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Typography variant="body2">{row.role}</Typography>
              </Grid>
              <Grid
                item
                xs={4}
                sx={{
                  p: 0,
                  borderRight: `1px solid ${theme.palette.action.disabled}`,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <AppTextField
                  fullWidth
                  value={row.name}
                  onChange={e =>
                    handleShareholderChange(index, 'name', e.target.value)
                  }
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  }}
                />
              </Grid>
              <Grid item xs={4} sx={{ p: 0 }}>
                <AppTextField
                  fullWidth
                  value={row.percentage}
                  onChange={e =>
                    handleShareholderChange(index, 'percentage', e.target.value)
                  }
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  }}
                />
              </Grid>
            </Grid>
          ))}
        </Box>
      </Grid>

      {/* Company Data Table */}
      <Grid container xs={12} sm={5.8} mb={isMobile ? 2 : 0}>
        <Box
          sx={{
            width: '100%',
            border: `1px solid ${theme.palette.action.disabled}`,
            height: 'fit-content',
          }}
        >
          {companyData.map((row, index) => (
            <Grid
              container
              key={index}
              sx={{
                borderBottom:
                  index !== companyData.length - 1
                    ? `1px solid ${theme.palette.action.disabled}`
                    : 'none',
                height: 'fit-content',
              }}
            >
              <Grid
                item
                xs={6}
                sx={{
                  p: 1,
                  borderRight: `1px solid ${theme.palette.action.disabled}`,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Typography variant="body2">{row.label}</Typography>
              </Grid>
              <Grid item xs={6} sx={{ p: 0 }}>
                <AppTextField
                  fullWidth
                  value={row.value}
                  onChange={e => handleCompanyDataChange(index, e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  }}
                />
              </Grid>
            </Grid>
          ))}
        </Box>
      </Grid>
    </Grid>
  );

  return (
    <Modal
      open={showModal}
      onClose={handleClose}
      disableEnforceFocus={true}
      disableBackdropClick={false}
      disableEscapeKeyDown={true}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      style={{
        backdropFilter: 'blur(5px)',
        backgroundColor: 'transparent',
        outline: 'none',
      }}
    >
      <Card sx={modalStyle}>
        <Grid py={4}>
          <Box
            sx={{
              flexDirection: 'row',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <H3>정보 수동 입력</H3>
            <Box
              onClick={handleClose}
              sx={{
                width: '32px',
                height: '32px',
                backgroundColor: theme.palette.borderColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 100,
                cursor: 'pointer',
                position: 'absolute',
                right: '32px',
              }}
            >
              <X color={theme.palette.grey400} />
            </Box>
          </Box>

          {/* Content */}
          <Box
            pt={1}
            pb={0}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Grid container gap={3} py={2}>
              <Grid item xs={12}>
                {renderTables()}
              </Grid>

              <Grid item xs={12} mt={2}>
                <Grid container gap={2}>
                  <Grid item xs={12} sm={5.8}>
                    <Button
                      onClick={handleClose}
                      variant="outlined"
                      sx={{
                        width: '100%',
                        borderColor: theme.palette.action.disabled,
                        color: theme.palette.text.primary,
                        '&:hover': {
                          borderColor: theme.palette.action.disabled,
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                    >
                      <ButtonText>취소</ButtonText>
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={5.8}>
                    <Button
                      onClick={handleSave}
                      variant="contained"
                      sx={{
                        width: '100%',
                        background: theme.palette.gradient.purpletoblue,
                        '&:hover': {
                          background: theme.palette.gradient.purpletoblue,
                        },
                      }}
                    >
                      <ButtonText>저장</ButtonText>
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Card>
    </Modal>
  );
}
